import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { CalendarClock, Search, ArrowUpDown, BookUser, Plane } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_FILTERS = [
  { v: 'all', label: 'All documents' },
  { v: 'passport', label: 'Passport' },
  { v: 'visa', label: 'Visa' },
];

const URGENCY_STYLE = {
  critical: { bg: '#FEE2E2', color: '#B91C1C', label: 'Critical' },
  warning: { bg: '#FEF3C7', color: '#B45309', label: 'Warning' },
  info: { bg: '#E0F2FE', color: '#0369A1', label: 'Upcoming' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const UrgencyBadge = ({ urgency, daysUntil }) => {
  const s = URGENCY_STYLE[urgency] || URGENCY_STYLE.info;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ backgroundColor: s.bg, color: s.color }}
      data-testid="doc-urgency-badge"
    >
      {daysUntil} day{daysUntil === 1 ? '' : 's'} left
    </span>
  );
};

const DocumentExpiry = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/document-expiry`)
      .then((res) => { if (alive) setRows(res.data?.items || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (typeFilter !== 'all' && r.docType !== typeFilter) return false;
      if (!q) return true;
      return [r.name, r.displayUCC, r.ucc, r.number].some((f) => (f || '').toLowerCase().includes(q));
    });
    return filtered.sort((a, b) => (sortAsc ? 1 : -1) * (a.expiryDate || '').localeCompare(b.expiryDate || ''));
  }, [rows, search, typeFilter, sortAsc]);

  const passportCount = rows.filter((r) => r.docType === 'passport').length;
  const visaCount = rows.filter((r) => r.docType === 'visa').length;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="document-expiry-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-teal-50 flex items-center justify-center">
          <CalendarClock className="h-5 w-5 text-teal-700" />
        </div>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Document Alerts</h1>
          <p className="text-sm text-slate-600">Passports expiring within 180 days · Visas within 60 days.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search traveller, UCC or document #…"
            className="pl-9 h-10"
            data-testid="doc-expiry-search"
          />
        </div>
        <div className="flex gap-2">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.v}
              type="button"
              onClick={() => setTypeFilter(f.v)}
              data-testid={`doc-type-filter-${f.v}`}
              className="px-3.5 h-10 rounded-lg text-sm font-medium border transition-colors"
              style={typeFilter === f.v
                ? { backgroundColor: '#0F6E56', color: '#fff', borderColor: '#0F6E56' }
                : { backgroundColor: '#fff', color: '#475569', borderColor: '#E2E8F0' }}
            >
              {f.label}
              {f.v === 'passport' ? ` (${passportCount})` : f.v === 'visa' ? ` (${visaCount})` : ` (${rows.length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="doc-expiry-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <div className="col-span-4">Traveller</div>
          <div className="col-span-2">Document</div>
          <div className="col-span-3">
            <button type="button" onClick={() => setSortAsc((s) => !s)} className="inline-flex items-center gap-1 hover:text-slate-800" data-testid="doc-sort-expiry">
              Expiry date <ArrowUpDown className="h-3 w-3" />
            </button>
          </div>
          <div className="col-span-3 text-right">Status</div>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="doc-expiry-loading">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-400" data-testid="doc-expiry-empty">
            No documents expiring in the alert window. You're all caught up.
          </div>
        ) : (
          visible.map((r, i) => (
            <button
              key={`${r.ucc}-${r.docType}-${i}`}
              type="button"
              onClick={() => navigate(`/clients/${r.ucc}`)}
              data-testid={`doc-expiry-row-${i}`}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center border-t border-slate-100 text-left hover:bg-slate-50 transition-colors"
            >
              <div className="col-span-4 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
                <p className="text-[11px] text-slate-400 font-mono">{r.displayUCC || r.ucc}</p>
              </div>
              <div className="col-span-2 flex items-center gap-1.5 text-sm text-slate-700">
                {r.docType === 'passport'
                  ? <BookUser className="h-4 w-4 text-slate-400" />
                  : <Plane className="h-4 w-4 text-slate-400" />}
                <span className="capitalize">{r.docType}</span>
              </div>
              <div className="col-span-3 text-sm text-slate-700">{fmtDate(r.expiryDate)}</div>
              <div className="col-span-3 flex justify-end">
                <UrgencyBadge urgency={r.urgency} daysUntil={r.daysUntil} />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentExpiry;
